import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ListOfRooms } from './components/list-of-rooms/list-of-rooms';
import { RoomsNotAvailable } from './components/rooms-not-available/rooms-not-available';
import { OccupancyReport } from './components/occupancy-report/occupancy-report';
import { WorkloadReport } from './components/workload-report/workload-report';
import { AppTabs } from '../../shared/components/app-tabs/app-tabs';
import { TranslateModule } from '@ngx-translate/core';
import { PageShell } from '../../shared/components/page-shell/page-shell';

const DEFAULT_TAB = 'list';

/** Query parameter carrying the active tab, e.g. /rooms?tab=workload */
const TAB_PARAM = 'tab';

@Component({
    selector: 'app-rooms',
    standalone: true,
    imports: [CommonModule, ListOfRooms, RoomsNotAvailable, OccupancyReport, WorkloadReport, AppTabs, TranslateModule, PageShell],
    templateUrl: './rooms.html',
})
export class RoomsComponent implements OnInit, OnDestroy {
    currentTab = DEFAULT_TAB;

    readonly tabItems = [
        { label: 'rooms_tab_list', value: 'list' },
        { label: 'rooms_tab_unavailable', value: 'unavailable' },
        { label: 'rooms_tab_occupancy', value: 'occupancy' },
        // Occupancy answers "was the room in use"; workload answers "was the
        // person overloaded". Different denominators, so a separate tab rather
        // than another grouping toggle inside the occupancy report.
        { label: 'rooms_tab_workload', value: 'workload' }
    ];

    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private destroy$ = new Subject<void>();

    ngOnInit() {
        // Subscribed rather than read once: the URL is the source of truth, so
        // a refresh, a pasted link and the browser's back button all land on the
        // same tab instead of silently snapping back to the first one.
        this.route.queryParamMap
            .pipe(takeUntil(this.destroy$))
            .subscribe(params => {
                const requested = params.get(TAB_PARAM);
                this.currentTab = this.isKnownTab(requested) ? requested! : DEFAULT_TAB;

                // A URL naming a tab that doesn't exist (a stale bookmark, a
                // renamed tab) would otherwise show the default while still
                // reading ?tab=something-else. Rewrite it so the address bar
                // never disagrees with what's on screen.
                if (requested !== null && !this.isKnownTab(requested)) {
                    this.writeTabToUrl(DEFAULT_TAB);
                }
            });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    selectTab(tabValue: string) {
        if (!this.isKnownTab(tabValue) || tabValue === this.currentTab) return;
        this.writeTabToUrl(tabValue);
    }

    /**
     * replaceUrl so flicking through tabs doesn't stack a history entry per
     * click — otherwise leaving the page means pressing back once for every tab
     * the user looked at. The trade-off is that back moves off /rooms rather
     * than to the previously viewed tab, which is the lesser surprise.
     *
     * queryParamsHandling 'merge' keeps any other query state on the route.
     */
    private writeTabToUrl(tabValue: string) {
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { [TAB_PARAM]: tabValue },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    private isKnownTab(value: string | null): boolean {
        return !!value && this.tabItems.some(t => t.value === value);
    }
}
